import {uniq} from 'lodash';
import {
  ClimateHazardsEvolutionGet,
  ClimateHazardsPeriod,
  ClimateHazardsScenario,
} from '../../../../../types/api/assets';

export const CLIMATE_HAZARDS_PERIODS: ClimateHazardsPeriod[] = ['near', 'medium', 'far']
export const CLIMATE_HAZARDS_SCENARIOS: ClimateHazardsScenario[] = ['ssp245', 'ssp370']

// blue / aqua / orange: distinguishable also with color vision deficiency
export const CLIMATE_HAZARDS_SERIES_COLORS = {
  t0: '#2a78d6',
  ssp245: '#1baf7a',
  ssp370: '#eb6834',
}

export type ClimateHazardsScale = 'adapted' | 'fixed'

// y-axis range: 0-10 or adapted to the plotted values. The max is always a tick, so the outer polygon shows its value
export function getClimateHazardsScaleRange(rows: ClimateHazardRow[], periods: ClimateHazardsPeriod[], scale: ClimateHazardsScale) {
  const ticks = (min: number, max: number, step: number) => {
    const tickPositions = []
    for (let tick = min; tick <= max + 1e-9; tick += step) {
      tickPositions.push(Math.round(tick * 100) / 100)
    }
    return {min, max, tickPositions}
  }
  if (scale === 'fixed' || !rows.length) {
    return ticks(0, 10, 2)
  }
  const values = rows.flatMap(row => [row.t0, ...periods.flatMap(period =>
    CLIMATE_HAZARDS_SCENARIOS.map(scenario => row.periods[period][scenario].value))])
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const range = Math.max(maxValue - minValue, 0.5)
  const step = range <= 1.5 ? 0.25 : range <= 3 ? 0.5 : 1
  // outer polygon = first tick above (or equal to) the max value, e.g. 4.85 -> 5
  const min = Math.max(0, Math.floor(minValue / step) * step)
  const max = Math.min(10, Math.ceil(maxValue / step) * step)
  return ticks(min, max > min ? max : min + step, step)
}

export type ClimateHazardScenarioValue = {
  value: number,
  // climate variable that determines the value, null when the hazard is not impacted by the selection
  ccv: string | null,
}

export type ClimateHazardRow = {
  hazardCode: string,
  t0: number,
  // true when at least one of the selected climate variables impacts the hazard
  impacted: boolean,
  periods: Record<ClimateHazardsPeriod, Record<ClimateHazardsScenario, ClimateHazardScenarioValue>>,
}

export function getClimateVariables(data: ClimateHazardsEvolutionGet): string[] {
  return uniq(CLIMATE_HAZARDS_PERIODS.flatMap(period =>
    data[period].flatMap(hazard => hazard.evos.map(evo => evo.ccv))
  ))
}

/*
 * For every hazard, period and scenario the value comes from the selected climate variable
 * with the largest deviation from t0. Hazards not impacted by any selected variable keep t0.
 */
export function computeClimateHazardRows(data: ClimateHazardsEvolutionGet, selectedCcv: string[]): ClimateHazardRow[] {
  return data.t0.map(({hazard_code, i_t0}) => {
    let impacted = false
    const periods = Object.fromEntries(CLIMATE_HAZARDS_PERIODS.map(period => {
      const evos = (data[period].find(h => h.hazard_code === hazard_code)?.evos || [])
        .filter(evo => selectedCcv.includes(evo.ccv))
      impacted = impacted || evos.length > 0
      const scenarios = Object.fromEntries(CLIMATE_HAZARDS_SCENARIOS.map(scenario => {
        let best: ClimateHazardScenarioValue = {value: i_t0, ccv: null}
        evos.forEach(evo => {
          const value = evo[`i_${scenario}_${period}`]
          if (typeof value === 'number' && (best.ccv === null || Math.abs(value - i_t0) > Math.abs(best.value - i_t0))) {
            best = {value, ccv: evo.ccv}
          }
        })
        return [scenario, best]
      }))
      return [period, scenarios]
    })) as ClimateHazardRow['periods']
    return {hazardCode: hazard_code, t0: i_t0, impacted, periods}
  })
}
